import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import ExportButton from './ExportButton';
import { 
  ClipboardCheck, CheckCircle2, XCircle, Clock, AlertTriangle,
  MapPin, User, Calendar, ChevronRight, Loader2, Camera,
  Download, FileText, BarChart3, RefreshCw, Search, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OSHAChecklistsPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [dueChecklists, setDueChecklists] = useState([]);
  const [stats, setStats] = useState({});
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [showChecklistDialog, setShowChecklistDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('due');
  
  // Form state
  const [location, setLocation] = useState('');
  const [project, setProject] = useState('');
  const [inspectorName, setInspectorName] = useState('');
  const [overallNotes, setOverallNotes] = useState('');
  const [checkedItems, setCheckedItems] = useState({});
  const [itemNotes, setItemNotes] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [dueRes, statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/checklists/due-today`, { headers }),
        axios.get(`${API}/api/checklists/stats`, { headers }),
        axios.get(`${API}/api/checklists/submissions/history?limit=20`, { headers })
      ]);
      
      setDueChecklists(dueRes.data.due_checklists || []);
      setStats(statsRes.data || {});
      setHistory(historyRes.data.submissions || []);
    } catch (error) {
      console.error('Error fetching checklist data:', error);
      toast({ title: 'Error', description: 'Failed to load checklists', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const openChecklist = async (checklist) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(
        `${API}/api/checklists/${checklist.checklist_id || checklist.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveChecklist(response.data);
      setCheckedItems({});
      setItemNotes({});
      setLocation('');
      setProject('');
      setOverallNotes('');
      setShowChecklistDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load checklist details', variant: 'destructive' });
    }
  };

  const handleItemCheck = (itemId, checked) => {
    setCheckedItems(prev => ({ ...prev, [itemId]: checked }));
  };

  const handleItemNotes = (itemId, notes) => {
    setItemNotes(prev => ({ ...prev, [itemId]: notes }));
  };

  const submitChecklist = async () => {
    if (!location || !inspectorName) {
      toast({ title: 'Error', description: 'Please fill in location and inspector name', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const items = activeChecklist.items.map(item => ({
        item_id: item.id,
        checked: checkedItems[item.id] || false,
        notes: itemNotes[item.id] || null
      }));

      const response = await axios.post(
        `${API}/api/checklists/submit`,
        {
          checklist_id: activeChecklist.checklist_id || activeChecklist.id,
          location,
          project,
          inspector_name: inspectorName,
          items,
          overall_notes: overallNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({ 
        title: response.data.status === 'pass' ? 'Checklist Passed!' : 'Checklist Submitted',
        description: `Compliance: ${response.data.compliance_score}% | ${response.data.findings_count} findings`
      });
      
      setShowChecklistDialog(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to submit checklist', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pass':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Pass</Badge>;
      case 'pass_with_findings':
        return <Badge className="bg-yellow-100 text-yellow-700"><AlertTriangle className="w-3 h-3 mr-1" />Pass w/ Findings</Badge>;
      case 'fail':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Fail</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const completedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalCount = activeChecklist?.items?.length || 0;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="osha-checklists-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardCheck className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">OSHA Checklists</h1>
              <p className="text-slate-500">Daily safety inspections & compliance tracking</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton dataType="checklists" label="Export" />
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.today?.due || 0}</div>
              <div className="text-sm text-slate-500">Due Today</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{stats.today?.submitted || 0}</div>
              <div className="text-sm text-slate-500">Completed</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{stats.month?.avg_compliance || 0}%</div>
              <div className="text-sm text-slate-500">Avg Compliance</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-600">{stats.month?.pass_rate || 0}%</div>
              <div className="text-sm text-slate-500">Pass Rate</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button 
          variant={activeTab === 'due' ? 'default' : 'outline'}
          onClick={() => setActiveTab('due')}
        >
          <Clock className="w-4 h-4 mr-2" />
          Due Today ({dueChecklists.length})
        </Button>
        <Button 
          variant={activeTab === 'history' ? 'default' : 'outline'}
          onClick={() => setActiveTab('history')}
        >
          <FileText className="w-4 h-4 mr-2" />
          History
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : activeTab === 'due' ? (
        /* Due Checklists */
        <div className="space-y-4">
          {dueChecklists.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900">All Caught Up!</h3>
                <p className="text-slate-500">No checklists due at this time.</p>
              </CardContent>
            </Card>
          ) : (
            dueChecklists.map(checklist => (
              <Card key={checklist.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => openChecklist(checklist)}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <ClipboardCheck className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{checklist.name}</h3>
                        <p className="text-sm text-slate-500">
                          {checklist.items?.length || 0} items • {checklist.frequency}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="capitalize">{checklist.frequency}</Badge>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      ) : (
        /* History */
        <div className="space-y-4">
          {history.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900">No History Yet</h3>
                <p className="text-slate-500">Complete your first checklist to see it here.</p>
              </CardContent>
            </Card>
          ) : (
            history.map(submission => (
              <Card key={submission.id}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{submission.checklist_name}</h3>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {submission.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {submission.inspector_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(submission.submitted_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-lg font-bold text-slate-900">{submission.compliance_score}%</div>
                        <div className="text-xs text-slate-500">Compliance</div>
                      </div>
                      {getStatusBadge(submission.status)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Checklist Dialog */}
      <Dialog open={showChecklistDialog} onOpenChange={setShowChecklistDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{activeChecklist?.name}</DialogTitle>
            <DialogDescription>
              Complete all items and submit when finished
            </DialogDescription>
          </DialogHeader>

          {activeChecklist && (
            <div className="space-y-6">
              {/* Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{completedCount} of {totalCount} items</span>
                  <span className="font-medium">{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </div>

              {/* Location & Inspector */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Location *</label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Building A, Floor 2"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Inspector Name *</label>
                  <Input
                    value={inspectorName}
                    onChange={(e) => setInspectorName(e.target.value)}
                    placeholder="Your name"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Project (Optional)</label>
                <Input
                  value={project}
                  onChange={(e) => setProject(e.target.value)}
                  placeholder="Project or work order"
                  className="mt-1"
                />
              </div>

              {/* Checklist Items */}
              <div className="space-y-3">
                <h4 className="font-medium text-slate-900 flex items-center justify-between">
                  <span>Inspection Items</span>
                  <span className="text-xs text-slate-500 font-normal">Tap to complete</span>
                </h4>
                {activeChecklist.items?.map((item, idx) => (
                  <div 
                    key={item.id} 
                    className={`p-4 rounded-lg border transition-all duration-200 active:scale-[0.98] ${
                      checkedItems[item.id] 
                        ? 'bg-green-50 border-green-300 shadow-sm' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleItemCheck(item.id, !checkedItems[item.id])}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                        checkedItems[item.id]
                          ? 'bg-green-500 border-green-500'
                          : 'border-slate-300'
                      }`}>
                        {checkedItems[item.id] && (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <span className={`text-slate-900 ${checkedItems[item.id] ? 'line-through text-slate-500' : ''}`}>
                            {item.text}
                          </span>
                          {item.critical && (
                            <Badge className="bg-red-100 text-red-700 text-xs flex-shrink-0">Critical</Badge>
                          )}
                        </div>
                        {!checkedItems[item.id] && (
                          <div className="mt-2 flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Input
                              placeholder="Notes (optional)..."
                              value={itemNotes[item.id] || ''}
                              onChange={(e) => handleItemNotes(item.id, e.target.value)}
                              className="text-sm flex-1"
                            />
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Camera functionality placeholder
                                toast({ title: 'Camera', description: 'Photo capture coming soon!' });
                              }}
                            >
                              <Camera className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Overall Notes */}
              <div>
                <label className="text-sm font-medium text-slate-700">Overall Notes (Optional)</label>
                <Textarea
                  value={overallNotes}
                  onChange={(e) => setOverallNotes(e.target.value)}
                  placeholder="Any additional observations..."
                  className="mt-1"
                  rows={3}
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowChecklistDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={submitChecklist} disabled={submitting}>
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" />Submit Checklist</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OSHAChecklistsPage;
